import React from "react";
import {
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  SimpleGrid,
  Code,
} from "@chakra-ui/react";
import CustomModal from "../../../CustomUi/CustomModal";
import CustomAlert from "../../../CustomUi/CustomAlert";
import CustomCode from "../../../CustomUi/CustomCode";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIcons from "../../../../hooks/useIcons";

interface AuditTrailEntry {
  id: string;
  entityName: string;
  entityId: string;
  action: "CREATE" | "UPDATE" | "DELETE";
  applicationUserId: string;
  oldValues: string;
  newValues: string;
  changes: string;
  ipAddress: string;
  userAgent: string;
  additionalData: string;
  createdAt: string;
}

interface AuditTrailDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedEntry: AuditTrailEntry | null;
  getUserInfo: (userId: string) => { name: string; email: string };
  getActionColor: (action: string) => string;
  formatDate: (dateString: string) => string;
  getChangedProperties: (
    oldValues: string,
    newValues: string
  ) => Array<{
    key: string;
    oldValue: any;
    newValue: any;
  }>;
}

const AuditTrailDetailModal: React.FC<AuditTrailDetailModalProps> = ({
  isOpen,
  onClose,
  selectedEntry,
  getUserInfo,
  getActionColor,
  formatDate,
  getChangedProperties,
}) => {
  const colors = useThemeColors();
  const icons = useIcons();

  if (!selectedEntry) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      handleClose={onClose}
      title="Audit Trail Details"
      icon={icons.main.audit}
      mode="view"
    >
      <VStack gap={6} align="stretch">
        {/* Basic Info */}
        <Box>
          <InlineSeparator label="Entry Information" />
          <SimpleGrid columns={[1, 2]} gap={4}>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                Action
              </Text>
              <Badge colorPalette={getActionColor(selectedEntry.action)}>
                {selectedEntry.action}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                Entity
              </Text>
              <Text>{selectedEntry.entityName}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                Entity ID
              </Text>
              <CustomCode value={selectedEntry.entityId} />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                User
              </Text>
              <VStack gap={1} align="start">
                <Text>{getUserInfo(selectedEntry.applicationUserId).name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {getUserInfo(selectedEntry.applicationUserId).email}
                </Text>
              </VStack>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                IP Address
              </Text>
              <Code fontSize="xs">{selectedEntry.ipAddress}</Code>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.500" mb={1}>
                Date
              </Text>
              <Text>{formatDate(selectedEntry.createdAt)}</Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />

        {/* User Agent */}
        <Box>
          <InlineSeparator label="Browser & Device Information" />
          <Code
            fontSize="xs"
            p={3}
            borderRadius="lg"
            display="block"
            bg={colors.background.tertiary}
          >
            {selectedEntry.userAgent}
          </Code>
        </Box>

        <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />

        {/* Changes */}
        <Box>
          <InlineSeparator label="Changes Summary" />
          <Box p={3} borderRadius="lg" bg={colors.background.tertiary}>
            <Text>{selectedEntry.changes}</Text>
          </Box>
        </Box>

        {/* Additional Data */}
        {selectedEntry.additionalData && (
          <>
            <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />
            <Box>
              <InlineSeparator label="Additional Data" />
              <Box p={3} borderRadius="lg" bg={colors.background.tertiary}>
                <Text fontSize="sm">{selectedEntry.additionalData}</Text>
              </Box>
            </Box>
          </>
        )}

        {/* Changed Properties - Only show for UPDATE and DELETE actions */}
        {(selectedEntry.oldValues || selectedEntry.newValues) &&
          selectedEntry.action !== "CREATE" && (
            <>
              <Box
                borderTop="1px"
                borderColor={colors.border.secondary}
                pt={4}
              />
              <Box>
                <InlineSeparator label="Changed Properties" />
                {(() => {
                  const changes = getChangedProperties(
                    selectedEntry.oldValues,
                    selectedEntry.newValues
                  );
                  if (changes.length === 0) {
                    return (
                      <CustomAlert
                        message="No specific property changes detected"
                        status="info"
                      />
                    );
                  }

                  return (
                    <VStack gap={3} align="stretch">
                      {changes.map((change, index) => (
                        <Box
                          key={index}
                          p={4}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={colors.border.subtle}
                          bg={colors.background.tertiary}
                        >
                          <HStack justify="space-between" align="start" mb={3}>
                            <Text fontSize="sm" fontWeight="bold">
                              {change.key}
                            </Text>
                            <Badge
                              size="sm"
                              colorPalette={
                                change.oldValue === null
                                  ? "green"
                                  : change.newValue === null
                                    ? "red"
                                    : "blue"
                              }
                            >
                              {change.oldValue === null
                                ? "Added"
                                : change.newValue === null
                                  ? "Removed"
                                  : "Modified"}
                            </Badge>
                          </HStack>

                          <SimpleGrid columns={[1, 2]} gap={3}>
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="medium"
                                color="red.500"
                                mb={1}
                              >
                                Previous Value
                              </Text>
                              <Box
                                p={2}
                                borderRadius="md"
                                bg={colors.status.errorBg}
                                border="1px solid"
                                borderColor={colors.status.errorBorder}
                              >
                                <Text fontSize="xs" fontFamily="mono">
                                  {change.oldValue === null ? (
                                    <Text
                                      as="span"
                                      color="gray.500"
                                      fontStyle="italic"
                                    >
                                      Not set
                                    </Text>
                                  ) : typeof change.oldValue === "object" ? (
                                    JSON.stringify(change.oldValue, null, 2)
                                  ) : (
                                    String(change.oldValue)
                                  )}
                                </Text>
                              </Box>
                            </Box>

                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="medium"
                                color="green.500"
                                mb={1}
                              >
                                New Value
                              </Text>
                              <Box
                                p={2}
                                borderRadius="md"
                                bg={colors.status.successBg}
                                border="1px solid"
                                borderColor={colors.status.successBorder}
                              >
                                <Text fontSize="xs" fontFamily="mono">
                                  {change.newValue === null ? (
                                    <Text
                                      as="span"
                                      color="gray.500"
                                      fontStyle="italic"
                                    >
                                      Not set
                                    </Text>
                                  ) : typeof change.newValue === "object" ? (
                                    JSON.stringify(change.newValue, null, 2)
                                  ) : (
                                    String(change.newValue)
                                  )}
                                </Text>
                              </Box>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      ))}
                    </VStack>
                  );
                })()}
              </Box>
            </>
          )}
      </VStack>
    </CustomModal>
  );
};

export default AuditTrailDetailModal;
